import {
  DEFAULT_TASK_SECTION_REFS,
  MoveTaskDto,
  Task,
  TaskBoard,
  TaskBoardsResponse,
  TaskCounts,
  TaskDashboardUser,
  TaskPriority,
  TaskSection,
  TaskSectionRef,
} from '../types';

export const TASK_PRIORITY_OPTIONS: Array<{
  value: TaskPriority;
  label: string;
  color: 'default' | 'info' | 'warning' | 'error';
}> = [
  { value: 'LOW', label: 'Low', color: 'info' },
  { value: 'NORMAL', label: 'Medium', color: 'default' },
  { value: 'HIGH', label: 'High', color: 'warning' },
  { value: 'URGENT', label: 'Urgent', color: 'error' },
];

export const TASK_SECTION_LABELS: Record<TaskSectionRef, string> = {
  triage: 'Triage',
  to_do: 'To Do',
  in_progress: 'In Progress',
  blocked: 'Blocked',
  escalated: 'Escalated',
  done: 'Done',
};

export type TaskDueStatus =
  | 'none'
  | 'completed'
  | 'overdue'
  | 'today'
  | 'upcoming';

export type TaskBoardColumn = Omit<TaskSection, 'tasks' | 'count'> & {
  tasks: Task[];
  count: number;
};

const TASK_SORT_FALLBACK_STEP = 1000;

export const DEFAULT_TASK_SECTIONS: TaskSection[] =
  DEFAULT_TASK_SECTION_REFS.map((ref, index) => ({
    id: -(index + 1),
    organisation_id: 0,
    name: TASK_SECTION_LABELS[ref],
    ref,
    position: (index + 1) * 10,
    terminal: ref === 'done',
    active: true,
    tasks: [],
    count: 0,
  }));

const isSameLocalDate = (left: Date, right: Date) =>
  left.getFullYear() === right.getFullYear() &&
  left.getMonth() === right.getMonth() &&
  left.getDate() === right.getDate();

const emptyCounts = (): TaskCounts => ({
  total: 0,
  overdue: 0,
  low: 0,
  normal: 0,
  high: 0,
  urgent: 0,
});

export const hasTaskManagerAccess = (
  isSuperAdmin = false,
  permissions: string[] = []
) => isSuperAdmin || permissions.includes('tasks.manage');

export const getTaskUserDisplayName = (
  user?: Partial<TaskDashboardUser> | null
) => {
  const displayName = user?.display_name?.trim();
  if (displayName) return displayName;

  const fullName = [user?.first_name, user?.last_name]
    .filter(Boolean)
    .join(' ')
    .trim();

  return fullName || user?.email || 'Dashboard user';
};

export const normalizeTaskAssigneeIds = (
  values?: Array<number | string | null | undefined> | null
) => {
  const seenIds = new Set<number>();

  return (values ?? []).reduce<number[]>((acc, value) => {
    const id = Number(value);
    if (!Number.isInteger(id) || id <= 0 || seenIds.has(id)) return acc;

    seenIds.add(id);
    acc.push(id);
    return acc;
  }, []);
};

export const getTaskAssigneeIds = (
  task: Pick<Task, 'assigned_dashboard_user_id' | 'assigned_dashboard_user_ids'>
) => {
  const assigneeIds = normalizeTaskAssigneeIds(
    task.assigned_dashboard_user_ids
  );

  if (assigneeIds.length) return assigneeIds;

  return normalizeTaskAssigneeIds([task.assigned_dashboard_user_id]);
};

export const getPrimaryTaskAssigneeId = (
  task: Pick<Task, 'assigned_dashboard_user_id' | 'assigned_dashboard_user_ids'>
) => getTaskAssigneeIds(task)[0] ?? null;

export const areTaskAssigneeIdsEqual = (
  left: number[] = [],
  right: number[] = []
) => {
  const normalizedLeft = normalizeTaskAssigneeIds(left);
  const normalizedRight = normalizeTaskAssigneeIds(right);

  return (
    normalizedLeft.length === normalizedRight.length &&
    normalizedLeft.every((id, index) => id === normalizedRight[index])
  );
};

export const sortTaskSections = (sections: TaskSection[] = []) =>
  [...sections]
    .filter((section) => section.active !== false)
    .sort(
      (left, right) => left.position - right.position || left.id - right.id
    );

export const sortTasksByOrder = (tasks: Task[] = []) =>
  [...tasks].sort(
    (left, right) => left.sort_order - right.sort_order || left.id - right.id
  );

export const getTaskDueStatus = (
  dueAt?: string | null,
  completedAt?: string | null,
  now = new Date()
): TaskDueStatus => {
  if (completedAt) return 'completed';
  if (!dueAt) return 'none';

  const dueDate = new Date(dueAt);
  if (Number.isNaN(dueDate.getTime())) return 'none';

  if (dueDate.getTime() < now.getTime()) return 'overdue';
  if (isSameLocalDate(dueDate, now)) return 'today';

  return 'upcoming';
};

export const getPriorityOption = (priority: TaskPriority) =>
  TASK_PRIORITY_OPTIONS.find((option) => option.value === priority) ??
  TASK_PRIORITY_OPTIONS[1];

export const toTaskBoardColumns = (
  board?: TaskBoard | null,
  fallbackSections: TaskSection[] = DEFAULT_TASK_SECTIONS
): TaskBoardColumn[] =>
  sortTaskSections(
    board?.sections?.length ? board.sections : fallbackSections
  ).map((section) => {
    const tasks = sortTasksByOrder(section.tasks ?? []);

    return {
      ...section,
      tasks,
      count: section.count ?? tasks.length,
    };
  });

export const calculateTaskSortOrder = (
  destinationTasks: Task[],
  destinationIndex: number,
  movingTaskId?: number | string
) => {
  const remainingTasks = sortTasksByOrder(
    destinationTasks.filter((task) => String(task.id) !== String(movingTaskId))
  );
  const previousTask = remainingTasks[destinationIndex - 1];
  const nextTask = remainingTasks[destinationIndex];

  if (previousTask && nextTask) {
    return (previousTask.sort_order + nextTask.sort_order) / 2;
  }

  if (previousTask) {
    return previousTask.sort_order + TASK_SORT_FALLBACK_STEP;
  }

  if (nextTask) {
    return nextTask.sort_order > TASK_SORT_FALLBACK_STEP
      ? nextTask.sort_order - TASK_SORT_FALLBACK_STEP
      : nextTask.sort_order / 2;
  }

  return TASK_SORT_FALLBACK_STEP;
};

const recalculateCounts = (sections: TaskSection[]): TaskCounts => {
  const counts = emptyCounts();

  sections.forEach((section) => {
    (section.tasks ?? []).forEach((task) => {
      counts.total += 1;
      if (getTaskDueStatus(task.due_at, task.completed_at) === 'overdue') {
        counts.overdue += 1;
      }

      const priority = task.priority.toLowerCase() as keyof Pick<
        TaskCounts,
        'low' | 'normal' | 'high' | 'urgent'
      >;
      counts[priority] += 1;
    });
  });

  return counts;
};

const withRecalculatedBoard = (board: TaskBoard): TaskBoard => {
  const sections = board.sections.map((section) => {
    const tasks = sortTasksByOrder(section.tasks ?? []);

    return {
      ...section,
      tasks,
      count: tasks.length,
    };
  });

  return {
    ...board,
    sections,
    counts: recalculateCounts(sections),
  };
};

const withRecalculatedMeta = (
  response: TaskBoardsResponse
): TaskBoardsResponse => ({
  ...response,
  data: response.data.map(withRecalculatedBoard),
  meta: {
    ...response.meta,
    totalBoards: response.data.length,
    totalTasks: response.data.reduce(
      (total, board) =>
        total +
        board.sections.reduce(
          (boardTotal, section) => boardTotal + (section.tasks ?? []).length,
          0
        ),
      0
    ),
  },
});

export const patchTaskInBoards = (
  response: TaskBoardsResponse | undefined,
  taskId: number,
  patch: Partial<Task>
): TaskBoardsResponse | undefined => {
  if (!response) return response;

  const nextResponse = {
    ...response,
    data: response.data.map((board) => ({
      ...board,
      sections: board.sections.map((section) => ({
        ...section,
        tasks: (section.tasks ?? []).map((task) =>
          task.id === taskId ? { ...task, ...patch } : task
        ),
      })),
    })),
  };

  return withRecalculatedMeta(nextResponse);
};

export const removeTaskFromBoards = (
  response: TaskBoardsResponse | undefined,
  taskId: number
): TaskBoardsResponse | undefined => {
  if (!response) return response;

  const nextResponse = {
    ...response,
    data: response.data.map((board) => ({
      ...board,
      sections: board.sections.map((section) => ({
        ...section,
        tasks: (section.tasks ?? []).filter((task) => task.id !== taskId),
      })),
    })),
  };

  return withRecalculatedMeta(nextResponse);
};

export const moveTaskInBoards = (
  response: TaskBoardsResponse | undefined,
  taskId: number,
  payload: MoveTaskDto,
  taskPatch: Partial<Task> = {}
): TaskBoardsResponse | undefined => {
  if (!response) return response;

  const source = response.data
    .flatMap((board, boardIndex) =>
      board.sections.flatMap((section) =>
        (section.tasks ?? []).map((task) => ({
          boardIndex,
          section,
          task,
        }))
      )
    )
    .find((item) => item.task.id === taskId);

  if (!source) return response;

  const sourceTask = source.task;

  const targetUserIds =
    payload.assigned_dashboard_user_ids !== undefined
      ? normalizeTaskAssigneeIds(payload.assigned_dashboard_user_ids)
      : getTaskAssigneeIds(sourceTask);
  const targetUserId = targetUserIds[0] ?? null;
  const matchingTargetBoardIndex =
    targetUserId == null
      ? undefined
      : response.data.findIndex(
          (board) => board.dashboard_user?.id === targetUserId
        );
  const targetBoardIndex =
    matchingTargetBoardIndex !== undefined && matchingTargetBoardIndex >= 0
      ? matchingTargetBoardIndex
      : response.data.length === 1
      ? source.boardIndex
      : undefined;

  if (targetBoardIndex === undefined) {
    return removeTaskFromBoards(response, taskId);
  }

  const targetBoard = response.data[targetBoardIndex];
  const targetSection = targetBoard.sections.find((section) => {
    if (payload.section_id !== undefined)
      return section.id === payload.section_id;
    if (payload.section_ref !== undefined)
      return section.ref === payload.section_ref;
    return section.id === sourceTask.section_id;
  });

  if (!targetSection) return response;

  const movedTask: Task = {
    ...sourceTask,
    ...taskPatch,
    assigned_dashboard_user_id: targetUserId,
    assigned_dashboard_user_ids: targetUserIds,
    section_id: targetSection.id,
    sort_order:
      payload.sort_order ?? taskPatch.sort_order ?? sourceTask.sort_order,
    completed_at: targetSection.terminal
      ? taskPatch.completed_at ??
        sourceTask.completed_at ??
        new Date().toISOString()
      : null,
  };

  const nextResponse = {
    ...response,
    data: response.data.map((board, boardIndex) => ({
      ...board,
      sections: board.sections.map((section) => {
        const withoutMovedTask = (section.tasks ?? []).filter(
          (task) => task.id !== taskId
        );
        const shouldInsert =
          boardIndex === targetBoardIndex && section.id === targetSection.id;

        return {
          ...section,
          tasks: shouldInsert
            ? sortTasksByOrder([...withoutMovedTask, movedTask])
            : withoutMovedTask,
        };
      }),
    })),
  };

  return withRecalculatedMeta(nextResponse);
};
